/**
 * UniversalEditorPage — host for the new editor-chrome stack.
 *
 * Wires the new EditorShell + DocChrome around the existing TipTapBlogEditor
 * for the `/editor/:contentType/:contentId` route. Reuses the same content
 * fetch + save plumbing that ContentEditorPage uses (useContent hook +
 * PATCH /blog-creation/content/:id), so the editor mounts with real data.
 *
 * The right rail is a placeholder until rail-builder mounts EditorRail in T5.
 * Inline suggestion underlines are wired in T6.
 *
 * The legacy `/content/editor/:contentId` route now permanently redirects
 * here (classic ContentEditorPage retired 2026-07-10).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  EditorShell,
  EditorChatPanel,
  DocChrome,
  InsertBlockBar,
  SideMiniRail,
  TweaksPanel,
  useEditorTweaks,
  type BreadcrumbCrumb,
  type FormatCommand,
  type ScoreBreakdownEntry,
} from '@/components/editor-chrome';
import {
  MarginSuggestions,
  RedlineBubbleMenu,
  SuggestionPopover,
  useDocSuggestionPopover,
  type MarginSuggestion,
  type SuggestionData,
} from '@/components/editor-chrome/overlays';
import {
  TipTapBlogEditor,
  type TipTapBlogEditorRef,
} from '@/components/tiptap-editor';
import type { Editor } from '@tiptap/react';
import type { RTEDocument } from '@/components/blog-templates';
import {
  applyTemplateStylesToContent,
  generateHTMLFromRTE,
} from '@/components/blog-templates';
import { useContent } from '@/hooks/useBlogCreationAPI';
import { useBlogTemplate } from '@/hooks/useBlogTemplates';
import { blogCreationService } from '@/services/blogCreationService';
import { isPublishCancelledError, buildGateItem } from '@/lib/prePublishGate';
import { summarizeGateIssues, type IssueBadgeSummary } from '@/lib/issueSummary';
import { useTenantStore } from '@/stores/useTenantStore';
import { activeTenantIdFromSelection, useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAgentFocus, type DirtyEdit } from '@/webmcp/focus';
import { useEditorBridge, type EditorBridge } from '@/webmcp/editorBridge';
import { useEditorMetadataDirty } from '@/webmcp/editorMetaDirty';
import type { TenantId } from '@/webmcp/contract';
import { useAuth } from '@/contexts/AuthContext';
import { UniversalEditorRailMount } from '@/components/editor-chrome/rail/UniversalEditorRailMount';
import { ReviewerSignoffCard } from '@/components/editor-chrome/rail/cards/ReviewerSignoffCard';
import { HumanVerifyModal } from '@/components/human-verify/HumanVerifyModal';
import {
  getHumanVerifyState,
  getUnverifiedHumanVerifyItems,
  type HumanVerifyItem,
} from '@/lib/humanVerify';
import type { HumanVerifyResolution } from '@/components/human-verify/HumanVerifyItemRow';
import { FixingOverlay } from '@/components/ui/FixingLoader';
import { ArticleSettingsDialog } from './ArticleSettingsDialog';
import { UploadCloud } from 'lucide-react';

const DEMO_FOCUS_KW = 'how long does a wood fence last';
const DEMO_SCORE = 88;
// Score is mocked until quality-gates hook lands in T5.
// TODO(t5): replace with useQualityReport(contentId).data?.overall_score
const DEMO_SCORE_BREAKDOWN: ScoreBreakdownEntry[] = [
  { label: 'SEO', value: 91 },
  { label: 'Auth.', value: 76 },
  { label: 'Struct.', value: 94 },
  { label: 'Judge', value: 82 },
];

// Mirrors editor-overlays.jsx SUGGESTIONS_BY_SID — keyed by data-sid on
// `.ed-suggestion` spans inside the doc. Until a real suggestions service
// lands, this stays as demo content so the popover surface is exercisable.
const DEMO_SUGGESTIONS_BY_SID: Record<string, SuggestionData> = {
  s1: {
    kind: 'tone',
    title: 'Tone — soften absolute claim',
    detail: 'Hedging this claim makes the article feel less promotional.',
    fix: 'Most homeowners',
  },
  s2: {
    kind: 'fact',
    title: 'Fact check',
    detail: 'No source for "20+ years" — cite a manufacturer warranty range.',
  },
  s3: {
    kind: 'grammar',
    title: 'Trim filler',
    detail: '"that" can be dropped without losing meaning.',
    fix: 'wood fence lasts',
  },
};

const DEMO_MARGIN_SUGGESTIONS: MarginSuggestion[] = [
  {
    id: 'm1',
    anchorSel: 'p:first-of-type',
    title: 'Use "fence post rot" in next paragraph',
    detail: 'Currently 0/2. Strong opportunity for SEO placement.',
    kind: 'topic',
    accent: 'depth',
  },
  {
    id: 'm2',
    anchorSel: 'p:nth-of-type(2)',
    title: 'Add a comparison table',
    detail: 'Cedar vs redwood vs PT pine works as a scannable element here.',
    kind: 'block',
    accent: 'coral',
  },
];

// ---------------------------------------------------------------------------
// Issue-badge jump cursor (PR-1 issue-visibility).
//
// The badge's onIssuesClick advances through the gate item's flagged fields
// and asks the rail to scroll each one into view. We reuse the rail's
// `[data-rail-section]` scroll mechanism by dispatching the same mini-rail
// jump event EditorRail already listens for (SideMiniRail.tsx → EditorRail).
//
// This field→section map mirrors POSTGEN_FIELD_TO_RAIL_SECTION in
// UniversalEditorRailMount.tsx:1132. Violation fields without a rail card
// (title/H1, focus_keyword, status, human_qa, human_verify) are skipped — the
// cursor advances to the next mappable field. Best-effort: never crashes.
const ISSUE_FIELD_TO_RAIL_SECTION: Record<string, string> = {
  meta_title: 'serp',
  meta_description: 'serp',
  url_slug: 'serp',
  og_title: 'social',
  og_description: 'social',
  twitter_title: 'social',
  twitter_description: 'social',
  excerpt: 'summary',
};

// Mirrors SideMiniRail.tsx MINI_RAIL_JUMP_EVENT. EditorRail listens and runs
// focusTools() + scrollToRailSection(sectionId) — panel-aware (switches to the
// Tools tab, then scrolls the card into view).
const MINI_RAIL_JUMP_EVENT = 'ed-mini-rail-jump';

function downloadBlob(filename: string, mime: string, contents: string) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Minimal HTML → Markdown shim for the export-md overflow action. We don't
 * want to pull in a turndown-grade dep just for one menu item; this handles
 * the common cases (headings, paragraphs, bold, italic, links, lists). The
 * "real" markdown export endpoint is owned by the backend — see TODO below.
 */
function htmlToMarkdown(html: string): string {
  return html
    .replace(/<h1[^>]*>(.*?)<\/h1>/gis, '# $1\n\n')
    .replace(/<h2[^>]*>(.*?)<\/h2>/gis, '## $1\n\n')
    .replace(/<h3[^>]*>(.*?)<\/h3>/gis, '### $1\n\n')
    .replace(/<strong[^>]*>(.*?)<\/strong>/gis, '**$1**')
    .replace(/<em[^>]*>(.*?)<\/em>/gis, '*$1*')
    .replace(/<a[^>]+href="([^"]+)"[^>]*>(.*?)<\/a>/gis, '[$2]($1)')
    .replace(/<li[^>]*>(.*?)<\/li>/gis, '- $1\n')
    .replace(/<\/?(ul|ol)[^>]*>/gi, '\n')
    .replace(/<p[^>]*>(.*?)<\/p>/gis, '$1\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function UniversalEditorPage() {
  const { contentId } = useParams<{
    contentType: string;
    contentId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // The list passes its exact URL (incl. ?page=N&view=...) in router state when
  // opening the editor. Both the Back button and the breadcrumbs return there,
  // so the user lands on the same page of the list they left. Falls back to
  // history (deep link within the app) then the bare list route (fresh tab).
  const fromListUrl =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? ((location.state as { from: string }).from)
      : undefined;
  const listUrl = fromListUrl ?? '/content/planner';

  const handleBack = useCallback(() => {
    if (fromListUrl) navigate(fromListUrl);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/content/planner');
  }, [fromListUrl, navigate]);

  const { currentTenant } = useTenantStore();
  const { user, session } = useAuth();
  // No fallback to the AgileCo test tenant: null until a client is selected.
  // Every sink below either tolerates null (service params typed
  // `string | null`, gate/html builders) or is guarded inline.
  const tenantId = currentTenant;
  const reviewer =
    user?.email ||
    session?.user?.email ||
    user?.id ||
    session?.user?.id ||
    '';

  const editorRef = useRef<TipTapBlogEditorRef>(null);
  const docContentRef = useRef<HTMLDivElement | null>(null);
  const [tiptapEditor, setTiptapEditor] = useState<Editor | null>(null);
  const [marginSuggestions, setMarginSuggestions] = useState<MarginSuggestion[]>(
    DEMO_MARGIN_SUGGESTIONS,
  );

  const docSuggestion = useDocSuggestionPopover({
    containerRef: docContentRef,
    suggestionsBySid: DEMO_SUGGESTIONS_BY_SID,
  });
  const applyDocSuggestion = useCallback(() => {
    // The popover is wired for `.ed-suggestion[data-sid]` spans rendered into
    // the doc surface. Real apply-fix needs a ProseMirror Decoration plugin
    // that maps the span back to a doc range — that's a follow-up. For now
    // we dismiss the visual suggestion and toast so the path is exercisable.
    if (!docSuggestion.active) return;
    docSuggestion.dismiss();
    toast('Apply fix — coming soon');
  }, [docSuggestion]);

  const {
    data: contentData,
    isLoading: isLoadingContent,
    error: contentError,
    refetch: refetchContent,
  } = useContent(contentId || null);

  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [humanVerifyModalOpen, setHumanVerifyModalOpen] = useState(false);
  const [humanVerifyInitialItemId, setHumanVerifyInitialItemId] = useState<string | null>(null);
  const [resolvingHumanVerifyItemId, setResolvingHumanVerifyItemId] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [headingCount, setHeadingCount] = useState(0);
  const { tweaks, setTweak, surfaceStyle, docFont } = useEditorTweaks();

  // Lifted so the rail Suggestions toggle and overlays-builder's suggestion
  // underlines (T6) read the same source of truth. Quiet AI presence forces
  // suggestions off; flipping to assistive/prominent restores the toggle.
  const [suggestionsOn, setSuggestionsOn] = useState(
    () => tweaks.aiPresence !== 'quiet',
  );

  useEffect(() => {
    setSuggestionsOn(tweaks.aiPresence !== 'quiet');
  }, [tweaks.aiPresence]);

  const title = titleOverride ?? contentData?.title ?? 'Untitled article';
  const focusKw =
    contentData?.target_keywords?.[0]?.keyword ??
    contentData?.keyword ??
    DEMO_FOCUS_KW;
  const initialContentJson = contentData?.content_json as RTEDocument | undefined;
  const wpPostId = contentData?.wordpress_post_id;
  const wpPostUrl = contentData?.wordpress_post_url;
  const isLiveOnWp =
    Boolean(wpPostId) && contentData?.wordpress_status === 'publish';
  const humanVerify = useMemo(
    () => getHumanVerifyState(contentData),
    [contentData],
  );

  // ---- Issue badge (PR-1 issue-visibility) -------------------------------
  // One unified count next to Publish, sourced from the same GateItem the
  // pre-publish gate dialog reads — so badge and dialog can never disagree.
  const issueGateItem = useMemo(
    () => (contentData ? buildGateItem(contentData, tenantId) : null),
    [contentData, tenantId],
  );
  const issueSummary: IssueBadgeSummary | undefined = useMemo(
    () => (issueGateItem ? summarizeGateIssues(issueGateItem) : undefined),
    [issueGateItem],
  );

  // Ordered list of rail sections that have ≥1 flagged field, deduped. Repeated
  // badge clicks advance through this list (cursor wraps around). When the
  // issue set changes the cursor modulo-wraps back into bounds automatically.
  const issueCursorRef = useRef(0);
  const mappableIssueSections = useMemo(() => {
    if (!issueGateItem) return [] as string[];
    const seen = new Set<string>();
    const sections: string[] = [];
    for (const violation of issueGateItem.violations) {
      const section = ISSUE_FIELD_TO_RAIL_SECTION[violation.field];
      if (!section || seen.has(section)) continue;
      seen.add(section);
      sections.push(section);
    }
    return sections;
  }, [issueGateItem]);

  const handleIssuesClick = useCallback(() => {
    const sections = mappableIssueSections;
    if (sections.length === 0) return;
    const idx = issueCursorRef.current % sections.length;
    issueCursorRef.current = (idx + 1) % sections.length;
    const sectionId = sections[idx];
    if (typeof window !== 'undefined') {
      // Panel-aware: EditorRail switches to Tools and scrolls the card in.
      window.dispatchEvent(
        new CustomEvent(MINI_RAIL_JUMP_EVENT, { detail: { sectionId } }),
      );
    }
  }, [mappableIssueSections]);

  // Template styles are applied to content_json before HTML generation so the
  // published output matches the canonical ContentEditorPage path
  // (ContentEditorPage.tsx). We publish from the UNFILTERED content_json (not
  // editor.getJSON(), which drops rawHtml/CTA nodes) so CTAs survive —
  // generateHTMLFromRTE() preserves them. See CLAUDE.md "CTA Handling".
  const { data: templateData } = useBlogTemplate(contentData?.template_id || null);
  const styledContentJson = useMemo<RTEDocument | undefined>(() => {
    if (!contentData?.content_json) return undefined;
    if (!templateData?.builder_blocks) {
      return contentData.content_json as RTEDocument;
    }
    return applyTemplateStylesToContent(
      contentData.content_json,
      templateData.builder_blocks,
    ) as RTEDocument;
  }, [contentData?.content_json, templateData?.builder_blocks]);

  const breadcrumbs = useMemo<BreadcrumbCrumb[]>(
    () => [
      // The client crumb is absent (not blank) while no client is selected
      // (ours). Both crumbs link back to wherever the user actually came
      // from — respecting list pagination/view state via listUrl — rather
      // than a hardcoded route (staging, commit adding fromListUrl/listUrl).
      ...(tenantId ? [{ label: tenantId, href: listUrl }] : []),
      { label: 'Articles', href: listUrl },
      { label: title },
    ],
    [tenantId, title, listUrl],
  );

  /**
   * Reuse the same PATCH endpoint ContentEditorPage uses
   * (frontend/src/pages/content/ContentEditorPage.tsx:373-408). Inline rather
   * than extracting a hook — extraction belongs in a dedicated cleanup task.
   */
  const handleSave = useCallback(
    async (json: RTEDocument, html: string, computedWordCount: number) => {
      if (!contentId) return;
      setIsSaving(true);
      try {
        // apiClient attaches the Authorization header — a raw fetch() here
        // 401s on prod and silently loses edits (auto-save AND the
        // save-before-publish step).
        await blogCreationService.patchContent(
          contentId,
          {
            content_json: json,
            content_html: html,
            total_word_count: computedWordCount,
            title,
          },
          tenantId,
        );
        setIsDirty(false);
        toast.success('Saved');
      } catch (err) {
        console.error('[UniversalEditorPage] save failed:', err);
        toast.error('Failed to save');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [contentId, tenantId, title],
  );

  const onSaveClick = useCallback(() => {
    editorRef.current?.saveNow().catch(() => {
      /* toast already fired in handleSave */
    });
  }, []);

  const openHumanVerifyModal = useCallback(
    (itemId?: string) => {
      setHumanVerifyInitialItemId(
        itemId ?? getUnverifiedHumanVerifyItems(humanVerify)[0]?.id ?? null,
      );
      setHumanVerifyModalOpen(true);
    },
    [humanVerify],
  );

  const handleHumanVerifyOpenChange = useCallback(
    (open: boolean) => {
      setHumanVerifyModalOpen(open);
      if (open || !searchParams.has('humanVerify')) return;

      const next = new URLSearchParams(searchParams);
      next.delete('humanVerify');
      next.delete('humanVerifyItem');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleResolveHumanVerify = useCallback(
    async (item: HumanVerifyItem, status: HumanVerifyResolution, note: string) => {
      if (!contentId) return;
      if (!reviewer) {
        toast.error('Reviewer identity is required before saving sign-off');
        return;
      }
      setResolvingHumanVerifyItemId(item.id);
      try {
        await blogCreationService.resolveHumanVerifyItem(
          contentId,
          {
            item_id: item.id,
            status,
            ...(note ? { note } : {}),
            reviewer,
          },
          tenantId,
        );
        await refetchContent();
        toast.success(status === 'verified' ? 'Reviewer item verified' : 'Reviewer item dismissed');
      } catch (err) {
        console.error('[UniversalEditorPage] human-verify resolve failed:', err);
        toast.error('Could not save reviewer sign-off');
      } finally {
        setResolvingHumanVerifyItemId(null);
      }
    },
    [contentId, refetchContent, reviewer, tenantId],
  );

  useEffect(() => {
    if (!searchParams.has('humanVerify')) return;
    setHumanVerifyInitialItemId(searchParams.get('humanVerifyItem'));
    setHumanVerifyModalOpen(true);
  }, [searchParams]);

  /**
   * Generate the authoritative publish HTML from content_json. Persists any
   * pending edits first so the published output reflects the latest doc.
   * Returns undefined when there's no content_json (backend then falls back).
   */
  const buildPublishHtml = useCallback(async (): Promise<string | undefined> => {
    if (isDirty) {
      await editorRef.current?.saveNow();
    }
    return styledContentJson ? generateHTMLFromRTE(styledContentJson, tenantId) : undefined;
  }, [isDirty, styledContentJson, tenantId]);

  // WebMCP editor surface. Derive the agent tenant the SAME way the runtime does
  // (`webmcp/runtime.ts` mints from the workspace selection), so the focus the
  // page publishes and the tenant the runtime checks it against are byte-equal;
  // a mismatch would read as `unknown` and refuse every editor write.
  const workspaceSelection = useWorkspaceStore((s) => s.selection);
  const agentTenant = useMemo<TenantId | null>(() => {
    const derived = activeTenantIdFromSelection(workspaceSelection);
    const website = typeof derived === 'string' ? derived.trim() : '';
    return website.length > 0 ? (website as TenantId) : null;
  }, [workspaceSelection]);

  // Publish focus on mount, unconditionally (the contract requires it: an
  // unpublished section reads as `unknown` and every write against it refuses).
  //
  // The `editor` section (toolbar, canvas, save, publish) is always clean: those
  // tools act on the live on-screen buffer through the same handlers the operator
  // uses, so there is no stale copy for an agent write to clobber. The
  // `editor-meta` section (the rail's metadata card) IS reported dirty while the
  // card holds unsaved edits its debounced save has not flushed — that is the one
  // buffer an agent metadata write (ed_set_canonical, …) could race, so its veto
  // must be real.
  const metadataDirty = useEditorMetadataDirty();
  const agentDirty = useMemo<DirtyEdit[]>(
    () =>
      metadataDirty && contentId
        ? [{ section: 'editor-meta', rowId: contentId, changeCount: 1 }]
        : [],
    [metadataDirty, contentId],
  );
  useAgentFocus({ tenant: agentTenant, surface: 'Blog editor', dirty: agentDirty });

  // Publish the editor's live handles so the WebMCP editor seam can reach the
  // TipTap editor and the save/publish paths from outside the React tree.
  const editorBridge = useMemo<EditorBridge | null>(() => {
    if (!contentId) return null;
    return {
      contentId,
      getEditor: () => editorRef.current?.getEditor() ?? null,
      saveNow: async () => {
        await editorRef.current?.saveNow();
      },
      buildPublishHtml,
    };
  }, [contentId, buildPublishHtml]);
  useEditorBridge(editorBridge);

  // Publish to WordPress (live). Wires the previously-stubbed publish button to
  // POST /blog-creation/content/:id/publish — mirrors ContentEditorPage.
  const handlePublish = useCallback(async () => {
    if (!contentId || isPublishing) return;
    setIsPublishing(true);
    const toastId = toast.loading('Publishing to WordPress…');
    try {
      const html = await buildPublishHtml();
      // publish_immediately=true — manual "Publish to CMS" means live publish,
      // not a WP draft (backend defaults to draft when the flag is omitted).
      const result = await blogCreationService.publish(contentId, html, true);
      await refetchContent();
      toast.success('Published to WordPress', {
        id: toastId,
        ...(result.post_url && {
          action: {
            label: 'View post',
            onClick: () =>
              window.open(result.post_url, '_blank', 'noopener,noreferrer'),
          },
        }),
      });
    } catch (err) {
      // User chose "Go back" in the pre-publish metadata gate — dismiss the
      // loading toast and abort without an error toast.
      if (isPublishCancelledError(err)) {
        toast.dismiss(toastId);
        return;
      }
      console.error('[UniversalEditorPage] publish failed:', err);
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Failed to publish to WordPress',
        { id: toastId },
      );
    } finally {
      setIsPublishing(false);
    }
  }, [contentId, isPublishing, buildPublishHtml, refetchContent]);

  // Preview the article in-app: render the exact publish HTML in a new tab.
  // WordPress drafts are NOT publicly viewable (/?p=<id>&preview=true 404s
  // without a WP login + preview nonce), so previewing the generated HTML
  // directly is reliable — no WP session needed, no draft created, and it shows
  // precisely what will be published. (Self-contained doc → inline hex is fine;
  // the app's theme tokens aren't available in a blob document.)
  const handlePreview = useCallback(async () => {
    // Prefer WordPress itself: it renders the article in the live theme —
    // real fonts, CTAs, related posts, plugin output — which the local render
    // below can only approximate. Published posts open at their permalink;
    // an article that exists on WP only as a draft opens through WP's own
    // preview (requires being signed in to WordPress in this browser).
    if (wpPostId) {
      // Open the tab synchronously, inside the click gesture — the draft sync
      // below is async, and a window.open issued after an await is what
      // pop-up blockers kill.
      const wpWin = window.open('', '_blank');
      if (!wpWin) {
        toast.error('Pop-up blocked — allow pop-ups to preview');
        return;
      }
      wpWin.opener = null;

      let target: string;
      if (isLiveOnWp && wpPostUrl) {
        target = wpPostUrl;
      } else {
        // Mirrors the 'open-wp' overflow action: the WP host is the public
        // URL's origin, falling back to the tenant domain.
        let base: string;
        try {
          base = wpPostUrl ? new URL(wpPostUrl).origin : `https://${tenantId}`;
        } catch {
          base = `https://${tenantId}`;
        }
        target = `${base}/?p=${wpPostId}&preview=true`;

        // Sync the WP draft from the saved content first, so the preview
        // shows what's on screen — not the last "Update on CMS". Mirrors
        // WordPress's own preview, which auto-saves the draft. The backend
        // refresh is builder-aware (Elementor/UX-Builder render from builder
        // meta, not post_content). Live posts are deliberately excluded:
        // previewing must never rewrite a published page.
        if (isDirty) {
          await editorRef.current?.saveNow();
        }
        if (contentId) {
          const syncToast = toast.loading('Syncing draft to WordPress…');
          try {
            await blogCreationService.previewSyncDraft(contentId, tenantId);
          } catch {
            toast.error(
              "Couldn't sync latest edits — preview shows WordPress's last saved version",
            );
          } finally {
            toast.dismiss(syncToast);
          }
        }
      }
      wpWin.location.href = target;
      return;
    }

    // Not on WordPress yet (nothing has been pushed), so there is no WP post
    // to open. Fall back to rendering the current editor content locally.
    const html = await buildPublishHtml();
    if (!html) {
      toast.error('Nothing to preview yet');
      return;
    }
    const doc = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Preview — ${title}</title>
<style>
  body { margin: 0; background: #f5f5f4; color: #1c1917;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    line-height: 1.7; }
  .preview-banner { max-width: 760px; margin: 0 auto; padding: 10px 24px;
    font: 600 12px/1.2 -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: .04em; color: #074E6F; background: #E6F4FB; }
  .preview { max-width: 760px; margin: 0 auto; padding: 40px 24px 96px;
    background: #fff; min-height: 100vh; box-shadow: 0 0 0 1px rgba(0,0,0,.05); }
  .preview img { max-width: 100%; height: auto; border-radius: 8px; }
  .preview h1, .preview h2, .preview h3 { line-height: 1.25; }
  .preview a { color: #075E85; }
</style>
</head>
<body>
<div class="preview-banner">PREVIEW — not yet published</div>
<article class="preview">${html}</article>
</body>
</html>`;
    const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error('Pop-up blocked — allow pop-ups to preview');
      return;
    }
    // Revoke once the new tab has had time to load the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [buildPublishHtml, title, wpPostId, wpPostUrl, isLiveOnWp, tenantId, contentId, isDirty]);

  const dismissMarginSuggestion = useCallback((id: string) => {
    setMarginSuggestions((arr) => arr.filter((s) => s.id !== id));
  }, []);

  const applyMarginSuggestion = useCallback((id: string) => {
    setMarginSuggestions((arr) => arr.filter((s) => s.id !== id));
    toast('Apply suggestion: coming soon');
  }, []);

  const handleFormat = useCallback((cmd: FormatCommand) => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    const chain = editor.chain().focus();
    switch (cmd) {
      case 'undo': chain.undo().run(); break;
      case 'redo': chain.redo().run(); break;
      case 'h1': chain.toggleHeading({ level: 1 }).run(); break;
      case 'h2': chain.toggleHeading({ level: 2 }).run(); break;
      case 'h3': chain.toggleHeading({ level: 3 }).run(); break;
      case 'bold': chain.toggleBold().run(); break;
      case 'italic': chain.toggleItalic().run(); break;
      case 'underline': chain.toggleUnderline().run(); break;
      case 'strike': chain.toggleStrike().run(); break;
      case 'bulletList': chain.toggleBulletList().run(); break;
      case 'orderedList': chain.toggleOrderedList().run(); break;
      case 'blockquote': chain.toggleBlockquote().run(); break;
      case 'code': chain.toggleCode().run(); break;
      case 'link': {
        const url = window.prompt('Link URL');
        if (url) chain.extendMarkRange('link').setLink({ href: url }).run();
        break;
      }
      case 'image': {
        const src = window.prompt('Image URL');
        if (src) chain.setImage({ src }).run();
        break;
      }
      case 'divider': chain.setHorizontalRule().run(); break;
      default: {
        const _exhaustive: never = cmd;
        void _exhaustive;
      }
    }
  }, []);

  // "Open in Google Doc" — open the stored doc, or generate it on the spot.
  // Existing-doc opens happen synchronously in the click, so pop-up blockers
  // allow them; the generate path opens after an await, where blockers CAN
  // intervene — hence the fallback toast carrying its own Open action.
  const [isExportingGdoc, setIsExportingGdoc] = useState(false);
  const handleOpenGoogleDoc = useCallback(async () => {
    const existing = contentData?.gdocs_doc_url;
    if (existing) {
      window.open(existing, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!contentId || isExportingGdoc) return;
    setIsExportingGdoc(true);
    try {
      const res = await blogCreationService.exportToGoogleDocs(contentId, tenantId);
      // Refresh the detail record so the next click short-circuits to the
      // stored URL instead of re-exporting.
      void refetchContent();
      const opened = window.open(res.gdocs_url, '_blank', 'noopener,noreferrer');
      if (opened) {
        toast.success('Google Doc generated successfully.');
      } else {
        toast.success('Your document is ready.', {
          action: {
            label: 'Open doc',
            onClick: () =>
              window.open(res.gdocs_url, '_blank', 'noopener,noreferrer'),
          },
          duration: 10_000,
        });
      }
    } catch (err) {
      console.error('[UniversalEditorPage] Google Doc export failed:', err);
      toast.error('Failed to generate Google Doc. Please try again.');
    } finally {
      setIsExportingGdoc(false);
    }
  }, [contentData?.gdocs_doc_url, contentId, tenantId, isExportingGdoc, refetchContent]);

  const handleOverflowAction = useCallback(
    (id: string) => {
      switch (id) {
        case 'view-published':
          if (wpPostUrl) {
            window.open(wpPostUrl, '_blank', 'noopener,noreferrer');
          } else {
            toast.error('No published URL — article has not been published yet');
          }
          break;
        case 'open-wp':
          if (wpPostId) {
            // WP admin host is the same as the public URL host. If wpPostUrl
            // is missing we fall back to https://<tenantId>; tenant's WP host
            // is set via reverse-proxy in production.
            const base = wpPostUrl
              ? new URL(wpPostUrl).origin
              : `https://${tenantId}`;
            window.open(
              `${base}/wp-admin/post.php?post=${wpPostId}&action=edit`,
              '_blank',
              'noopener,noreferrer',
            );
          } else {
            toast.error('Not connected to WordPress yet');
          }
          break;
        case 'export-html': {
          const html = editorRef.current?.getHTML();
          if (!html) {
            toast.error('Editor not ready');
            return;
          }
          downloadBlob(`${title}.html`, 'text/html;charset=utf-8', html);
          break;
        }
        case 'export-md': {
          const html = editorRef.current?.getHTML();
          if (!html) {
            toast.error('Editor not ready');
            return;
          }
          downloadBlob(
            `${title}.md`,
            'text/markdown;charset=utf-8',
            htmlToMarkdown(html),
          );
          break;
        }
        // TODO: backend endpoint POST /blog-creation/content/:id/duplicate
        case 'duplicate':
          toast('Duplicate article — coming soon');
          break;
        // TODO: dedicated history route /content/editor/:contentId/history
        case 'history':
          toast('Article history — coming soon');
          break;
        // TODO: backend endpoint PATCH /blog-creation/content/:id { folder_id }
        case 'move-folder':
          toast('Move to folder — coming soon');
          break;
        // WS-E: slug/URL editing lives here (categories / featured image TBD).
        case 'settings':
          setSettingsOpen(true);
          break;
        // TODO: backend endpoint DELETE /blog-creation/content/:id with confirm
        case 'delete':
          toast('Delete article — coming soon');
          break;
        default:
          console.warn('[UniversalEditorPage] unknown overflow action', id);
      }
    },
    [tenantId, title, wpPostId, wpPostUrl],
  );

  if (!contentId) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Universal Editor: No content selected</p>
      </div>
    );
  }

  if (contentError) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        <p>Failed to load content: {String((contentError as Error).message)}</p>
      </div>
    );
  }

  const topChrome = (
    <DocChrome
      breadcrumbs={breadcrumbs}
      onBack={handleBack}
      unsaved={isDirty}
      saving={isSaving}
      score={DEMO_SCORE}
      scoreGrade="B+"
      scoreDescription="Strong on SEO + structure. One authority citation away from an A."
      scoreBreakdown={DEMO_SCORE_BREAKDOWN}
      title={title}
      onTitleChange={(value) => {
        setTitleOverride(value);
        setIsDirty(true);
      }}
      focusKeyword={focusKw}
      wordCount={wordCount}
      charCount={charCount}
      onSave={onSaveClick}
      onPreview={handlePreview}
      onPublish={handlePublish}
      publishLabel={isLiveOnWp ? 'Update on CMS' : 'Publish to CMS'}
      publishLoading={isPublishing}
      onViewPost={
        isLiveOnWp && wpPostUrl
          ? () => window.open(wpPostUrl, '_blank', 'noopener,noreferrer')
          : undefined
      }
      onFormat={handleFormat}
      onOpenGoogleDoc={handleOpenGoogleDoc}
      gdocLoading={isExportingGdoc}
      overflowProps={{ onAction: handleOverflowAction }}
      suggestContentId={contentId}
      suggestTenantId={tenantId ?? undefined}
      metadataGenerating={
        contentData?.metadata_generation_status === 'queued' ||
        contentData?.metadata_generation_status === 'running'
      }
      issueSummary={issueSummary}
      onIssuesClick={handleIssuesClick}
      extraRows={
        <>
          {/* Write-with-AI CTA removed (broken). */}
          <InsertBlockBar
            onInsert={(id) => {
              // TODO(t5): wire to TipTap insert commands per block type.
              console.debug('[UniversalEditorPage] insert block:', id);
            }}
          />
        </>
      }
    />
  );

  return (
    <>
    <EditorShell
      topChrome={topChrome}
      miniRail={<SideMiniRail />}
      chatPanel={<EditorChatPanel />}
      rightRail={
        <UniversalEditorRailMount
          contentId={contentId}
          wordCount={wordCount}
          charCount={charCount}
          headingCount={headingCount}
          suggestionsOn={suggestionsOn}
          setSuggestionsOn={setSuggestionsOn}
          tiptapEditor={tiptapEditor}
          getEditorSnapshot={() => ({
            html: editorRef.current?.getHTML(),
            json: editorRef.current?.getJSON(),
          })}
          trailingTools={
            <>
              <ReviewerSignoffCard
                humanVerify={humanVerify}
                resolvingItemId={resolvingHumanVerifyItemId}
                onResolve={handleResolveHumanVerify}
                onOpenModal={openHumanVerifyModal}
              />
              <HumanVerifyModal
                open={humanVerifyModalOpen}
                onOpenChange={handleHumanVerifyOpenChange}
                humanVerify={humanVerify}
                initialItemId={humanVerifyInitialItemId}
                resolvingItemId={resolvingHumanVerifyItemId}
                onResolve={handleResolveHumanVerify}
              />
            </>
          }
        />
      }
    >
      <div
        data-ai-presence={tweaks.aiPresence}
        className={
          'ed-doc relative mx-auto max-w-[1100px] px-8 py-8' +
          (suggestionsOn ? '' : ' ed-no-sug')
        }
        style={{ ...surfaceStyle, fontFamily: docFont }}
      >
        <FixingOverlay
          show={isPublishing}
          label="Publishing to WordPress…"
          icon={UploadCloud}
        />
        <MarginSuggestions
          docRef={docContentRef}
          suggestions={marginSuggestions}
          onApply={applyMarginSuggestion}
          onDismiss={dismissMarginSuggestion}
        />
        <div ref={docContentRef}>
          {isLoadingContent ? (
            <div className="text-sm text-muted-foreground">Loading article…</div>
          ) : (
            <>
              <TipTapBlogEditor
                ref={editorRef}
                contentId={contentId}
                templateId={contentData?.template_id}
                initialContentJson={initialContentJson}
                initialContent={contentData?.content_html}
                focusTerms={focusKw ? [focusKw] : []}
                autoSave
                hideInternalToolbar
                onSave={handleSave}
                onEditorReady={setTiptapEditor}
                onChange={(html) => {
                  setIsDirty(true);
                  const text = html.replace(/<[^>]*>/g, '').trim();
                  setCharCount(text.length);
                  setWordCount(text ? text.split(/\s+/).length : 0);
                  setHeadingCount((html.match(/<h[1-6]\b/gi) ?? []).length);
                }}
                onH1Change={(h1) => {
                  if (h1 && h1 !== title) setTitleOverride(h1);
                }}
              />
              <div>
                <RedlineBubbleMenu editor={tiptapEditor} />
                <SuggestionPopover
                  open={docSuggestion.open && suggestionsOn}
                  anchorEl={docSuggestion.active?.anchorEl ?? null}
                  suggestion={docSuggestion.active?.data ?? null}
                  onApply={applyDocSuggestion}
                  onDismiss={() => docSuggestion.dismiss()}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </EditorShell>
    <ArticleSettingsDialog
      open={settingsOpen}
      onOpenChange={setSettingsOpen}
      contentId={contentId}
      initialSlug={contentData?.url_slug ?? ''}
      wordpressPostId={wpPostId}
      wordpressPostUrl={wpPostUrl}
      onSaved={refetchContent}
    />
    {import.meta.env.VITE_EDITOR_TWEAKS === 'true' && (
      <TweaksPanel tweaks={tweaks} setTweak={setTweak} />
    )}
    </>
  );
}
